/**
 * OAuth consent for an AI app (MCP client) signing in to MiraOps. The app's /authorize call
 * redirects here with mcp_* parameters; the signed-in member picks a workspace, narrows the
 * scopes and approves. MiraOps then returns to the app's registered address with a single-use
 * code bound to the PKCE challenge. Unregistered apps or return addresses are never redirected to.
 */
import { failureText, openFailure } from '../lib/failure-text.ts';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, MCP_SCOPES, approveMcpOAuth, classifyFailure, listMyWorkspaces, retryRead, readMcpOAuthClient, type McpOAuthClient, type McpScope, type Workspace } from '@miraops/contracts';
import { transport } from '../lib/session.ts';
import { useI18n, type MessageKey } from '../lib/i18n.tsx';
import { Banner, Button, Checkbox, Segmented, Spinner } from '../ui/ui.tsx';
import { Wordmark } from '../ui/icons.tsx';

export interface McpAuthRequest { clientId: string; redirectUri: string; codeChallenge: string; scopes: string[]; state: string | null }

const KEY = 'miraops.mcpAuthorize';

/** Reads and removes the consent parameters from the address bar. The request is kept for this
 *  tab until it is answered, so signing in with an emailed link doesn't lose it. */
export function takeMcpAuthRequest(): McpAuthRequest | null {
  const q = new URLSearchParams(window.location.search);
  if (q.get('mcp_authorize') !== '1') {
    try { const saved = sessionStorage.getItem(KEY); return saved ? JSON.parse(saved) as McpAuthRequest : null; } catch { /* ignored: browser storage blocked: no request kept from earlier in this tab */  return null; }
  }
  const request = {
    clientId: q.get('mcp_client_id') ?? '', redirectUri: q.get('mcp_redirect_uri') ?? '', codeChallenge: q.get('mcp_code_challenge') ?? '',
    scopes: (q.get('mcp_scope') ?? 'read').split(/\s+/).filter(Boolean), state: q.get('mcp_state'),
  };
  window.history.replaceState(null, '', window.location.pathname);
  try { sessionStorage.setItem(KEY, JSON.stringify(request)); } catch { /* kept in memory only */ }
  return request;
}
export function forgetMcpAuthRequest() { try { sessionStorage.removeItem(KEY); } catch { /* nothing stored */ } }

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function returnTo(request: McpAuthRequest, params: Record<string, string>) {
  forgetMcpAuthRequest();
  const url = new URL(request.redirectUri);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  if (request.state) url.searchParams.set('state', request.state);
  window.location.assign(url.toString());
}

export function McpConsent({ request, email, onClose }: { request: McpAuthRequest; email: string; onClose: () => void }) {
  const { t } = useI18n();
  const [client, setClient] = useState<McpOAuthClient | null | undefined>(undefined);
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [workspaceId, setWorkspaceId] = useState('');
  const requested = useMemo(() => MCP_SCOPES.filter(s => s === 'read' || request.scopes.includes(s)), [request.scopes]);
  const [scopes, setScopes] = useState<Set<McpScope>>(() => new Set(requested));
  const [days, setDays] = useState<'7' | '30' | '90'>('30');
  const [failure, setFailure] = useState<string | null>(null);
  const [clientFailure, setClientFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!UUID.test(request.clientId) || !/^https?:\/\//.test(request.redirectUri)) { setClient(null); return; }
    // Only a not-found or refused answer makes the request invalid; offline or busy says so, with Try again.
    retryRead(() => readMcpOAuthClient(transport, request.clientId, request.redirectUri)).then(setClient, error => { const why = openFailure(t, error); if (why !== true) setClientFailure(why); setClient(null); });
    retryRead(() => listMyWorkspaces(transport)).then(list => {
      const open = list.filter(w => w.available);
      setWorkspaces(open);
      setWorkspaceId(open.slice().sort((a, b) => (b.lastOpenedAt ?? 0) - (a.lastOpenedAt ?? 0))[0]?.id ?? '');
    }, error => { setFailure(failureText(t, classifyFailure({ error, operation: 'read' }))); setWorkspaces([]); });
  }, [request.clientId, request.redirectUri]);

  const valid = client && client.redirect_ok && /^[A-Za-z0-9_-]{43}$/.test(request.codeChallenge);
  const allow = async () => {
    setBusy(true); setFailure(null);
    try {
      const code = await approveMcpOAuth(transport, workspaceId, request.clientId, request.redirectUri, request.codeChallenge, [...scopes], Number(days));
      returnTo(request, { code });
    } catch (e) {
      setBusy(false);
      setFailure(e instanceof ApiError && e.token === 'access_denied' ? t('mcpauth.denied') : t('mcpauth.failed', { code: e instanceof ApiError ? e.token ?? String(e.status) : 'network' }));
    }
  };
  const toggle = (s: McpScope, on: boolean) => setScopes(prev => { const next = new Set(prev); if (on) next.add(s); else next.delete(s); next.add('read'); return next; });

  return (
    <div className="public">
      <header className="chooser-head"><Wordmark size="lg" /></header>
      <main className="chooser mcpauth" aria-labelledby="mcpauth-title">
        <div className="chooser-body mcpauth-body">
          {client === undefined ? <div className="center"><Spinner label={t('state.loading')} /></div>
          : clientFailure ? (
            <><Banner tone="danger">{clientFailure}</Banner>
              <div className="mcpauth-actions"><Button variant="primary" onClick={() => location.reload()}>{t('state.retry')}</Button></div></>
          ) : !valid ? (
            <>
              <h1 id="mcpauth-title">{t('mcpauth.invalidTitle')}</h1>
              <Banner tone="danger">{t('mcpauth.invalid')}</Banner>
              <div className="mcpauth-actions"><Button variant="primary" onClick={onClose}>{t('mcpauth.continue')}</Button></div>
            </>
          ) : (
            <>
              <h1 id="mcpauth-title">{t('mcpauth.title', { app: client.client_name })}</h1>
              <p className="muted">{t('mcpauth.subtitle', { app: client.client_name, email })}</p>
              {failure && <Banner tone="danger">{failure}</Banner>}
              {workspaces === null ? <Spinner label={t('state.loading')} /> : workspaces.length === 0 ? (failure ? null : <Banner tone="attention">{t('mcpauth.noWorkspaces')}</Banner>) : (
                <>
                  <label className="field">
                    <span className="field-label">{t('mcpauth.workspace')}</span>
                    <select className="field-input" value={workspaceId} onChange={e => setWorkspaceId(e.target.value)}>
                      {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </label>
                  <fieldset className="mcp-scopes"><legend>{t('mcpauth.scopes')}</legend>
                    {requested.map(s => (
                      <div key={s} className="mcp-scope">
                        <Checkbox checked={scopes.has(s)} onChange={on => toggle(s, on)} label={t(`mcp.scope.${s}` as MessageKey)} hideLabel={false} />
                        <span className="muted small">{t(`mcp.scope.${s}.detail` as MessageKey)}</span>
                      </div>
                    ))}
                  </fieldset>
                  <div className="field">
                    <span className="field-label">{t('mcpauth.days')}</span>
                    <Segmented<'7' | '30' | '90'> label={t('mcpauth.days')} value={days} onChange={setDays}
                      options={(['7', '30', '90'] as const).map(n => ({ value: n, label: t('mcp.days', { n }) }))} />
                  </div>
                </>
              )}
              <p className="muted small">{t('mcpauth.returnTo', { host: client.redirect_host ?? '' })} {t('mcpauth.revokeHint')}</p>
              <div className="mcpauth-actions">
                <Button onClick={() => returnTo(request, { error: 'access_denied' })} disabled={busy}>{t('mcpauth.deny')}</Button>
                <Button variant="primary" onClick={() => void allow()} disabled={busy || !workspaceId}>{t('mcpauth.allow')}</Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
